import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';

const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api'
  : '/api';

const WS_URL = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'ws://localhost:8001'
  : `wss://${window.location.host}`;

// TL kategorileri - bu kategoriler dışındakiler USD
const TL_CATEGORIES = ['BIST', 'BIST 100', 'BIST 30', 'BIST_100', 'BIST_30', 'BIST YILDIZ', 'BIST ANA', 'BORSA', 'HISSE', 'VIOP', 'DOVIZ', 'FOREX'];

// Varlık USD mi TRY mi?
const isUsdAsset = (position) => {
  const category = position.category || '';
  const currency = position.currency || '';
  // Eğer currency belirtilmişse ona bak
  if (currency === 'USD') return true;
  if (currency === 'TRY') return false;
  // Kategori bazında kontrol
  return !TL_CATEGORIES.includes(category);
};

function OpenPositions({ onRefresh, user, onPositionClick, refresh, onAutoClose }) {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState({ show: false, positionId: null, position: null });
  const [resultModal, setResultModal] = useState({ show: false, success: true, message: '' });
  // İŞ A — Kısmi kapanış modal state
  const [partialModal, setPartialModal] = useState({ show: false, position: null, ratio: 0.5, estimatedPnl: null, previewLoading: false });
  // İŞ B — Tek kaynak "Toplam Varlık" — backend'den okur
  const [accountSummary, setAccountSummary] = useState(null);
  const [usdTryRate, setUsdTryRate] = useState(0); // Faz 2 Paket 4 — sabit fallback kaldırıldı, backend response'undan gelir
  const token = localStorage.getItem('token');
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const positionsRef = useRef(positions);
  
  // İŞ B — Account summary fetch (tek kaynak "Toplam Varlık" ve "İşlem Gücü")
  useEffect(() => {
    let cancelled = false;
    const fetchSummary = async () => {
      try {
        const t = localStorage.getItem('token');
        if (!t) return;
        const r = await axios.get(`${API}/account-summary`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        if (!cancelled) setAccountSummary(r.data);
      } catch (err) {
        // sessizce geç — özet gelmezse UI '...' gösterir
      }
    };
    fetchSummary();
    const iv = setInterval(fetchSummary, 8000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [positions.length, refresh]);

  // Positions değiştiğinde ref'i güncelle
  useEffect(() => {
    positionsRef.current = positions;
  }, [positions]);

  // USD/TRY kurunu çek
  useEffect(() => {
    const fetchUsdRate = async () => {
      try {
        const response = await axios.get(`${API}/exchange-rate`);
        if (response.data && response.data.usd_try_rate) {
          setUsdTryRate(response.data.usd_try_rate);
          console.log('💱 USD/TRY Rate:', response.data.usd_try_rate);
        }
      } catch (error) {
        console.warn('USD/TRY kuru alınamadı');
      }
    };
    fetchUsdRate();
    // Her 5 dakikada bir güncelle
    const interval = setInterval(fetchUsdRate, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchPositions = async () => {
    console.log('📋 Fetching positions...');
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.error('❌ No token found in localStorage');
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/positions`, {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 30000 // 30 saniye timeout
      });
      
      console.log('✅ Positions API response:', response.data?.length || 0, 'positions');
      
      if (response.data && Array.isArray(response.data)) {
        setPositions(response.data);
        console.log('📋 Positions loaded:', response.data.length);
      } else {
        console.warn('⚠️ Unexpected response format:', response.data);
        setPositions([]);
      }
    } catch (error) {
      console.error('❌ Pozisyonlar yüklenemedi:', error.message);
      console.error('❌ Error details:', error.response?.status, error.response?.data);
      
      // Token geçersizse yeniden giriş yaptır
      if (error.response?.status === 401) {
        console.error('❌ Token expired or invalid');
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      
      setPositions([]);
    } finally {
      setLoading(false);
    }
  };

  // Faz 2 Paket 2.2 — updateLivePrices KALDIRILDI.
  // Backend /api/market/realtime-price artık DB'den okuyor; fetchPositions zaten
  // her 5 sn'de yeni fiyatları getiriyor. Ayrı polling gerekmez.
  const updateLivePrices = async () => {
    // no-op — fiyat tek kanaldan (fetchPositions) geliyor
    return;
  };

  // Refresh when `refresh` prop changes
  useEffect(() => {
    if (refresh !== undefined) {
      console.log(' Refresh triggered by parent, fetching positions...');
      fetchPositions();
    }
  }, [refresh]);

  // Pozisyon sayısını izle - SL/TP ile kapanan pozisyonları algıla
  const previousPositionCount = useRef(positions.length);
  const previousPositionIds = useRef(new Set());
  
  useEffect(() => {
    const currentIds = new Set(positions.map(p => p.id));
    const prevIds = previousPositionIds.current;
    
    // Eğer bir pozisyon kapandıysa (ID listeden çıktıysa)
    if (prevIds.size > 0 && currentIds.size < prevIds.size) {
      const closedIds = [...prevIds].filter(id => !currentIds.has(id));
      if (closedIds.length > 0) {
        console.log('🔴 Pozisyon(lar) kapandı (muhtemelen SL/TP):', closedIds.length);
        
        // Bildirim göster
        setResultModal({
          show: true,
          success: true,
          message: `⚠️ ${closedIds.length} pozisyon otomatik kapandı (SL/TP tetiklendi)`
        });
        
        // Bakiyeyi güncelle - parent'a haber ver
        if (onRefresh) {
          onRefresh();
        }
        
        // 2 saniye sonra tamamlananlara geç
        setTimeout(() => {
          setResultModal({ show: false, success: true, message: '' });
          if (onAutoClose) {
            onAutoClose();
          }
        }, 2000);
      }
    }
    
    previousPositionCount.current = positions.length;
    previousPositionIds.current = currentIds;
  }, [positions, onRefresh, onAutoClose]);

  useEffect(() => {
    // Initial fetch
    fetchPositions();
    
    // İlk fiyat güncellemesini hemen yap (1 saniye sonra - pozisyonlar yüklendikten sonra)
    const initialUpdate = setTimeout(() => {
      updateLivePrices();
    }, 1000);
    
    // Fiyat güncellemeleri - HER 5 SANİYEDE (SL/TP'yi hızlı algılamak için)
    const priceUpdateInterval = setInterval(() => {
      updateLivePrices();
    }, 5000);
    
    // Pozisyon kontrolü - HER 5 SANİYEDE (SL/TP ile kapananları algıla)
    const positionCheckInterval = setInterval(() => {
      fetchPositions();
    }, 5000);
    
    return () => {
      clearTimeout(initialUpdate);
      clearInterval(priceUpdateInterval);
      clearInterval(positionCheckInterval);
    };
  }, []);  // Sadece mount'ta çalış

  // Pozisyon kapatma onay modal'ını aç
  const handleCloseClick = (positionId, position) => {
    setConfirmModal({ show: true, positionId, position });
  };

  // Pozisyonu gerçekten kapat
  const closePosition = async (positionId) => {
    setConfirmModal({ show: false, positionId: null, position: null });
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.error('❌ No token for closing position');
      return;
    }
    
    try {
      console.log('🔒 OpenPositions: Closing position:', positionId);
      const response = await axios.post(`${API}/close-position/${positionId}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log(' OpenPositions: Position closed successfully:', response.data);
      
      // ANLIK GÜNCELLEME: Pozisyonu hemen listeden kaldır
      setPositions(prev => prev.filter(p => p.id !== positionId));
      
      // Trigger parent refresh
      if (onRefresh) {
        await onRefresh();
      }
      
      // Site içi sonuç bildirimi
      const pnlFormatted = formatCurrency(response.data.pnl, user);
      const balanceFormatted = formatCurrency(response.data.new_balance, user);
      setResultModal({ 
        show: true, 
        success: true, 
        message: `Kar/Zarar: ${pnlFormatted}\nYeni Bakiye: ${balanceFormatted}` 
      });
      
      // Arka planda pozisyon listesini yenile
      fetchPositions();
    } catch (error) {
      console.error(' OpenPositions: Close position error:', error);
      setResultModal({ 
        show: true, 
        success: false, 
        message: error.response?.data?.detail || error.message 
      });
    }
  };

  const calculatePnL = (position) => {
    // ✅ HER ZAMAN BACKEND'DEN GELEN DEĞER KULLANILACAK
    // Backend USD/TRY dönüşümünü, kaldıraçları doğru hesaplıyor
    // Frontend tekrar hesaplama yapmamalı - tutarsızlık oluşur
    if (position.profit_loss !== undefined && position.profit_loss !== null) {
      return position.profit_loss;
    }
    
    // Fallback: Backend değeri yoksa hesapla (nadiren gerekir)
    const currentPrice = position.live_price || position.current_price;
    const entryPrice = position.avg_buy_price || position.entry_price;
    
    if (!currentPrice || !entryPrice || currentPrice <= 0 || entryPrice <= 0) {
      return 0;
    }
    
    const priceDiff = currentPrice - entryPrice;
    // YENİ MATEMATİK: amount artık `adet` (kaldıraç içerir). K/Z çarpımı YOK.
    const kurAcilis = position.kur_acilis || (isUsdAsset(position) ? usdTryRate : 1);
    const pnl = priceDiff * (position.amount || position.quantity) * kurAcilis;
    
    return pnl;
  };
  
  const calculatePnLPercent = (position) => {
    // ✅ HER ZAMAN BACKEND'DEN GELEN DEĞER KULLANILACAK
    if (position.profit_loss_percent !== undefined && position.profit_loss_percent !== null) {
      return position.profit_loss_percent;
    }
    
    // Fallback: teminata göre yüzde (kaldıraç ile çarpma YOK — spec)
    const currentPrice = position.live_price || position.current_price;
    const entryPrice = position.avg_buy_price || position.entry_price;
    
    if (!entryPrice || entryPrice <= 0 || !currentPrice || currentPrice <= 0) {
      return 0;
    }
    
    // pnl / teminat × 100 — daha anlamlı: kullanıcı ne kadar teminatın ne kadarını kazandı
    const teminat = position.teminat || position.locked_amount || 0;
    if (teminat > 0) {
      const pnl = calculatePnL(position);
      return (pnl / teminat) * 100;
    }
    // Teminat yoksa (BIST spot) fiyat yüzdesi
    return ((currentPrice - entryPrice) / entryPrice) * 100;
  };
  
  const getCurrentPrice = (position) => {
    return position.live_price || position.current_price || position.avg_buy_price || 0;
  };

  // Calculate total P&L BEFORE any conditional returns
  const totalPnL = useMemo(() => {
    const validPnLs = positions.map(pos => calculatePnL(pos)).filter(pnl => pnl !== null);
    if (validPnLs.length === 0) return null; // Hala yükleniyor
    return validPnLs.reduce((sum, pnl) => sum + pnl, 0);
  }, [positions]);

  if (loading) {
    return <div className="text-gray-400 text-center py-4">Yükleniyor...</div>;
  }

  if (positions.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 text-center">
        <div className="text-gray-400 mb-2">🔍</div>
        <div className="text-gray-400">Açık pozisyon bulunmuyor</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Özet — İŞ A: Pozisyon Değeri kaldırıldı (notional sızıntısı) */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-750 rounded-lg p-4 border border-gray-700">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div>
            <div className="text-gray-400 text-xs sm:text-sm">📊 Açık Pozisyon</div>
            <div className="text-white text-lg sm:text-xl font-bold">{positions.length}</div>
          </div>
          <div>
            <div className="text-gray-400 text-xs sm:text-sm">📈 Toplam K/Z</div>
            <div className={`text-lg sm:text-xl font-bold ${totalPnL === null ? 'text-gray-400' : (totalPnL >= 0 ? 'text-green-400' : 'text-red-400')}`}>
              {totalPnL === null ? (
                <span className="animate-pulse">...</span>
              ) : (
                <>{totalPnL >= 0 ? '+' : ''}{formatCurrency(totalPnL, user)}</>
              )}
            </div>
          </div>
          <div className="bg-green-900/30 rounded p-2">
            <div className="text-green-300 text-xs sm:text-sm">💎 Toplam Varlık</div>
            <div className="text-green-400 text-lg sm:text-xl font-bold" data-testid="oplists-varlik">
              {accountSummary === null ? (
                <span className="animate-pulse text-gray-400">...</span>
              ) : (
                formatCurrency(accountSummary.varlik, user)
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pozisyon Listesi - MOBİL UYUMLU */}
      <div className="space-y-1 max-h-[300px] sm:max-h-[400px] overflow-y-auto overflow-x-hidden pb-2">
        {positions.map(position => {
          // Recalculate P&L with latest live prices
          const symbol = position.asset_symbol || position.symbol;
          const livePrice = position.live_price;
          const pnl = calculatePnL(position);
          const pnlPercent = calculatePnLPercent(position);
          
          // Log for debugging
          if (livePrice) {
            console.log(`📊 Rendering ${symbol}: Entry=${position.avg_buy_price}, Live=${livePrice}, P&L=${pnl.toFixed(2)}`);
          }

          return (
            <div 
              key={position.id} 
              className="bg-gray-800 rounded-lg p-2 sm:p-2.5 w-full cursor-pointer hover:bg-gray-700 transition-colors"
              onClick={() => {
                console.log('🖱️ Position clicked:', position.asset_symbol || position.symbol);
                if (onPositionClick) {
                  onPositionClick(position);
                } else {
                  console.error(' onPositionClick callback not provided!');
                }
              }}
            >
              {/* Hisse Adı ve Sembol - KOMPAKT */}
              <div className="flex justify-between items-start gap-1 sm:gap-2 mb-1 sm:mb-1.5 w-full">
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-xs sm:text-sm truncate">{position.asset_symbol || position.symbol}</div>
                  <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                    <span className={`text-[9px] sm:text-xs px-1 py-0.5 rounded font-bold shrink-0 ${
                      position.type === 'LONG' ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'
                    }`}>
                      {position.type === 'LONG' ? '📈 AL' : '📉 SAT'}
                    </span>
                    {/* İŞ B.1 — adet yalnız hisse grubunda (L=1) gösterilir;
                        kaldıraçlı gruplarda notional türetilebilirliğini engelle */}
                    {position.grup === 'hisse' && (
                      <span className="text-gray-400 text-[9px] sm:text-xs">
                        {position.amount || position.quantity} adet
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPartialModal({ show: true, position, ratio: 0.5, estimatedPnl: null, previewLoading: false });
                    }}
                    className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-orange-600 text-white text-[9px] sm:text-xs rounded"
                    data-testid={`partial-close-btn-${position.id}`}
                    title="Kısmi kapat"
                  >
                    Kısmi
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent triggering position click
                      handleCloseClick(position.id, position);
                    }}
                    className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-red-600 text-white text-[9px] sm:text-xs rounded"
                    data-testid={`close-btn-${position.id}`}
                  >
                    Kapat
                  </button>
                </div>
              </div>

              {/* Fiyatlar ve K/Z - KOMPAKT */}
              <div className="grid grid-cols-3 gap-1 text-[9px] sm:text-xs w-full">
                <div>
                  <div className="text-gray-500 text-[8px] sm:text-[10px]">Giriş</div>
                  <div className="text-white font-mono text-[9px] sm:text-xs">
                    {/* USD varlıklar (EMTIA, KRIPTO, FOREX vb.) için $ göster */}
                    {isUsdAsset(position)
                      ? `$${(position.avg_buy_price || position.entry_price || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                      : formatCurrency(position.avg_buy_price || position.entry_price || 0, user)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 text-[8px] sm:text-[10px]">Şimdi</div>
                  <div className="text-white font-mono font-bold text-[9px] sm:text-xs">
                    {isUsdAsset(position)
                      ? `$${getCurrentPrice(position).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                      : formatCurrency(getCurrentPrice(position), user)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-gray-500 text-[8px] sm:text-[10px]">K/Z</div>
                  {pnl === null ? (
                    <div className="text-gray-400 text-[9px] sm:text-xs animate-pulse">Hesaplanıyor...</div>
                  ) : (
                    <>
                      <div className={`font-bold text-[9px] sm:text-xs ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {pnl >= 0 ? '+' : ''}
                        {/* K/Z her zaman TL olarak gösterilir - USD varlıklar için kur çarpımı yapılmış olmalı */}
                        ₺{Math.abs(pnl).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </div>
                      <div className={`text-[8px] sm:text-xs ${pnlPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {pnlPercent >= 0 ? '+' : ''}{(pnlPercent || 0).toFixed(2)}%
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Stop Loss / Take Profit - HEP TL OLARAK GÖSTER */}
              {(position.stop_loss || position.take_profit) && (
                <div className="flex gap-2 sm:gap-4 mt-1 sm:mt-2 pt-1 sm:pt-2 border-t border-gray-700 text-[9px] sm:text-xs">
                  {position.stop_loss && (
                    <div>
                      <span className="text-gray-400">SL:</span>
                      <span className="text-red-400 ml-1">
                        ₺{(position.stop_loss || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                    </div>
                  )}
                  {position.take_profit && (
                    <div>
                      <span className="text-gray-400">TP:</span>
                      <span className="text-green-400 ml-1">
                        ₺{(position.take_profit || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* İŞ A — Kısmi Kapanış Modal'ı */}
      {partialModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl" data-testid="partial-close-modal">
            <div className="text-center">
              <div className="text-3xl mb-2">✂️</div>
              <h3 className="text-white font-bold text-lg mb-1">Kısmi Kapat</h3>
              <p className="text-gray-400 text-xs mb-4">
                <span className="text-white font-medium">{partialModal.position?.symbol}</span> pozisyonunun ne kadarını kapatmak istersiniz?
              </p>
              <div className="grid grid-cols-4 gap-2 mb-4">
                {[0.25, 0.5, 0.75, 1.0].map(r => (
                  <button
                    key={r}
                    onClick={() => setPartialModal(m => ({ ...m, ratio: r, estimatedPnl: null }))}
                    className={`py-2 rounded font-bold text-sm transition-colors ${
                      partialModal.ratio === r
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    }`}
                    data-testid={`partial-ratio-${Math.round(r*100)}`}
                  >
                    %{Math.round(r * 100)}
                  </button>
                ))}
              </div>
              {/* Serbest kaydırıcı */}
              <div className="mb-4">
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={Math.round(partialModal.ratio * 100)}
                  onChange={(e) => setPartialModal(m => ({ ...m, ratio: parseInt(e.target.value)/100, estimatedPnl: null }))}
                  className="w-full accent-orange-500"
                  data-testid="partial-ratio-slider"
                />
                <div className="text-orange-400 font-mono text-sm mt-1" data-testid="partial-ratio-display">
                  Kapatılacak: pozisyonun %{Math.round(partialModal.ratio * 100)}'i
                </div>
                <div className="text-gray-500 text-xs">
                  Kalan pozisyon: %{100 - Math.round(partialModal.ratio * 100)}
                </div>
              </div>
              {/* Tahmini K/Z — backend'den */}
              <div className="mb-4 p-2 bg-gray-900 rounded" data-testid="partial-pnl-preview">
                {partialModal.previewLoading ? (
                  <span className="text-gray-400 text-sm animate-pulse">Tahmin yükleniyor...</span>
                ) : partialModal.estimatedPnl !== null && partialModal.estimatedPnl !== undefined ? (
                  <div className={`font-mono text-base font-bold ${partialModal.estimatedPnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    Tahmini K/Z: {partialModal.estimatedPnl >= 0 ? '+' : '−'}
                    {Math.abs(partialModal.estimatedPnl).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} TL
                  </div>
                ) : (
                  <button
                    onClick={async () => {
                      setPartialModal(m => ({ ...m, previewLoading: true }));
                      try {
                        const t = localStorage.getItem('token');
                        const r = await axios.post(`${API}/trade/estimate-pnl`, {
                          position_id: partialModal.position.id,
                          close_ratio: partialModal.ratio,
                        }, { headers: { Authorization: `Bearer ${t}` } });
                        setPartialModal(m => ({ ...m, estimatedPnl: r.data.tp_estimated_pnl, previewLoading: false }));
                      } catch (err) {
                        setPartialModal(m => ({ ...m, previewLoading: false }));
                      }
                    }}
                    className="text-orange-400 text-sm hover:underline"
                  >
                    Tahmini K/Z'yi göster
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setPartialModal({ show: false, position: null, ratio: 0.5, estimatedPnl: null, previewLoading: false })}
                  className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                  data-testid="partial-close-cancel"
                >
                  İptal
                </button>
                <button
                  onClick={async () => {
                    const pos = partialModal.position;
                    const ratio = partialModal.ratio;
                    setPartialModal({ show: false, position: null, ratio: 0.5, estimatedPnl: null, previewLoading: false });
                    try {
                      const t = localStorage.getItem('token');
                      const resp = await axios.post(`${API}/close-position/${pos.id}`, { close_ratio: ratio }, { headers: { Authorization: `Bearer ${t}` } });
                      const pnlFormatted = formatCurrency(resp.data.pnl, user);
                      const balanceFormatted = formatCurrency(resp.data.new_balance, user);
                      setResultModal({
                        show: true, success: true,
                        message: `%${Math.round(ratio*100)} kapatıldı\nKar/Zarar: ${pnlFormatted}\nYeni Bakiye: ${balanceFormatted}`,
                      });
                      if (onRefresh) await onRefresh();
                      fetchPositions();
                    } catch (err) {
                      setResultModal({
                        show: true, success: false,
                        message: err?.response?.data?.detail || 'Kısmi kapanış başarısız',
                      });
                    }
                  }}
                  className="flex-1 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors"
                  data-testid="partial-close-confirm"
                >
                  Onayla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pozisyon Kapatma Onay Modal'ı */}
      {confirmModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">⚠️</div>
              <h3 className="text-white font-bold text-lg mb-2">Pozisyonu Kapat</h3>
              <p className="text-gray-400 text-sm mb-4">
                <span className="text-white font-medium">{confirmModal.position?.symbol}</span> pozisyonunu kapatmak istediğinizden emin misiniz?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmModal({ show: false, positionId: null, position: null })}
                  className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={() => closePosition(confirmModal.positionId)}
                  className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sonuç Modal'ı */}
      {resultModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">{resultModal.success ? '✅' : '❌'}</div>
              <h3 className="text-white font-bold text-lg mb-2">
                {resultModal.success ? 'Pozisyon Kapatıldı' : 'Hata'}
              </h3>
              <p className={`text-sm mb-4 whitespace-pre-line ${resultModal.success ? 'text-green-400' : 'text-red-400'}`}>
                {resultModal.message}
              </p>
              <button
                onClick={() => setResultModal({ show: false, success: true, message: '' })}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OpenPositions;
